'use client';

import { PencilSimpleIcon } from '@c/icons';
import ListingActionButtons from '@c/listing/ListingActionButtons';
import { useQuery } from '@tanstack/react-query';
import { getMainCatsNew } from '@util/firestore/categories';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { useCreateListing } from '../_context';
import { BikeFitment } from './BikeFitment';
import BikeLocation from './BikeLocation';
import BikePackage from './BikePackage';
import EditCategoryModal from './EditCategoryModal';
import FeaturesSection from './FeaturesSection';
import { GearFitment } from './GearFitment';
import { MediaSelect } from './MediaSelect';
import { ProductInfo } from './ProductInfo';
import { ProductPriceAndInventory } from './ProductPriceAndInventory';
import { Shipping } from './Shipping';

export default function CreateListingStep() {
  const {
    form,
    onSubmit,
    onSubmitError,
    saveDraft,
    submitting,
    product,
    variant,
  } = useCreateListing();
  const [editCategoryModalOpen, setEditCategoryModalOpen] = useState(false);

  const [isOtherBrand, setIsOtherBrand] = useState(false);
  const [otherBrand, setOtherBrand] = useState('');

  const { data: sports } = useQuery({
    queryKey: ['sports'],
    queryFn: () => getMainCatsNew(),
  });

  const mediaSelectRef = useRef<HTMLDivElement>(null);
  const productInfoRef = useRef<HTMLDivElement>(null);
  const gearFitmentRef = useRef<HTMLDivElement>(null);
  const bikeFitmentRef = useRef<HTMLDivElement>(null);
  const featuresSectionRef = useRef<HTMLDivElement>(null);
  const shippingRef = useRef<HTMLDivElement>(null);
  const bikePackageRef = useRef<HTMLDivElement>(null);
  const priceRef = useRef<HTMLDivElement>(null);

  const categoriesDisplay = product.categories.join(' > ');

  const handleSubmitClick = async () => {
    const categories = form.getValues('categories') || [];
    form.setValue('categories', categories);

    const brand = form.getValues('brand').toLowerCase();

    if (brand === 'other') {
      form.setValue('brand', otherBrand);
    }

    await form.handleSubmit(onSubmit, onSubmitError)();
  };

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const errors = form.formState.errors;
    const hasMediaErrors = errors.images || errors.video;
    const hasProductInfoErrors =
      errors.title || errors.description || errors.brand || errors.condition;
    const hasGearFitmentErrors = errors.size || errors.variations;
    const hasFeaturesSectionErrors =
      errors.floor_price || errors.minimum_offer_price;
    const hasShippingErrors = 'shipping' in errors;
    const hasPriceErrors = errors.price;
    const hasBikeErrors = 'fitting_bikes' in errors;
    const hasSourcesErrors = 'sources' in errors;

    if (hasMediaErrors) {
      mediaSelectRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasProductInfoErrors) {
      productInfoRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasGearFitmentErrors) {
      gearFitmentRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasFeaturesSectionErrors) {
      featuresSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    if (hasShippingErrors) {
      shippingRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    if (hasPriceErrors || hasSourcesErrors) {
      priceRef.current?.scrollIntoView({ behavior: 'smooth' });
    }

    if (hasBikeErrors) {
      bikeFitmentRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [form.formState.errors]);

  const checkIncludes = (includes: string[]) =>
    includes.some((include) =>
      product?.category.toLowerCase().includes(include.toLowerCase())
    );

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, onSubmitError)}
      className={`flex w-full flex-col gap-4 px-8 py-16 sm:mx-auto sm:max-w-content sm:px-0 sm:py-32`}
    >
      <div className="flex items-center gap-x-4">
        <h1 className="text-[3rem] font-semibold">{'Create a listing'}</h1>

        {product.is_draft && (
          <div className="flex items-center justify-center rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] font-medium text-white">
            Draft
          </div>
        )}
        {(product.integration_info?.type === 'ebay' ||
          product.integration_info?.type === 'shopify') && (
          <div className="flex items-center justify-center rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] font-medium text-white">
            This item is externally synced and cannot be edited from MX Locker
          </div>
        )}
      </div>

      <div className="mt-8 flex w-full flex-col items-start justify-between overflow-hidden rounded-[1.5rem] border border-zinc-200 bg-white py-8 sm:flex-row">
        <div className="flex w-full items-center gap-x-8">
          <Image
            src={
              sports?.find((c) => {
                return c.product_cats.ride_type === product.ride_types?.[0];
              })?.image ||
              sports?.sort((a, b) => a.index! - b.index!)[0]?.image ||
              ''
            }
            alt={form.watch('ride_types')[0]}
            width={200}
            height={200}
            className="-ml-40 h-64 w-auto object-cover"
            priority
          />

          <div className="flex flex-col gap-y-4">
            <h3 className="text-[2rem] font-semibold text-black">
              Sports & Categories
            </h3>

            <div className="flex flex-col gap-y-2">
              <h3 className="text-[2.4rem] font-semibold !capitalize text-brand-secondary">
                {product.ride_types.join(' > ')}
              </h3>

              <span className="text-[1.8rem] !capitalize text-black">
                {categoriesDisplay}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-8 flex w-full justify-end pr-16 sm:mt-0 sm:w-auto sm:justify-normal">
          <div
            className="flex cursor-pointer items-center gap-x-4 rounded-2xl bg-brand-secondary px-4 py-2 text-[1.8rem] text-white sm:gap-x-2 sm:rounded-none sm:bg-inherit sm:px-4 sm:py-0 sm:text-black"
            onClick={() => {
              setEditCategoryModalOpen(true);
            }}
          >
            <PencilSimpleIcon />
            Edit
          </div>
          <EditCategoryModal
            isOpen={editCategoryModalOpen}
            dismiss={() => setEditCategoryModalOpen(false)}
          />
        </div>
      </div>

      <div ref={mediaSelectRef}>
        <MediaSelect />
      </div>

      <div ref={productInfoRef}>
        <ProductInfo
          otherBrand={otherBrand}
          setOtherBrand={setOtherBrand}
          isOtherBrand={isOtherBrand}
          setIsOtherBrand={setIsOtherBrand}
        />
      </div>

      <div ref={priceRef}>
        <ProductPriceAndInventory />
      </div>

      {checkIncludes(['dirt bikes']) && <BikeLocation />}

      {checkIncludes(['parts']) &&
        !product.ride_types.includes('Cycling') &&
        !product.ride_types.includes('Snow') && (
          <div ref={bikeFitmentRef}>
            <BikeFitment />
          </div>
        )}

      {checkIncludes(['apparel', 'riding gear', 'casual', 'collectibles']) &&
        product.category1 !== 'Goggles & Accessories' && (
          <div ref={gearFitmentRef}>
            <GearFitment />
          </div>
        )}

      <div ref={featuresSectionRef}>
        <FeaturesSection />
      </div>

      {checkIncludes(['dirt bikes']) ? (
        <div ref={bikePackageRef}>
          <BikePackage />
        </div>
      ) : (
        !checkIncludes(['giveaways']) && (
          <div ref={shippingRef}>
            <Shipping />
          </div>
        )
      )}

      <div className="m-16">
        <ListingActionButtons
          variant={variant}
          product={product}
          saveDraft={saveDraft}
          onSubmitClick={handleSubmitClick}
          isLoading={submitting}
          isDraft={product.is_draft}
        />
      </div>
    </form>
  );
}
